import { ComponentType, CSSProperties, useEffect, useMemo, useRef } from 'react';
import { first } from 'rxjs/operators';
import { GridApi, ICellRendererParams, IDatasource } from 'ag-grid-community';
import { GridView, LoadIndicatorView } from '@vivli/shared/views';
import { GridCellRendererEnum, LoadIndicatorSizeEnum } from '@vivli/shared/infrastructure/enum';
import { IDataRequestSummary } from '@vivli/features/data-requests/infrastructure/interface';
import { DataRequestListTabEnum, DataRequestSummarySortFieldEnum } from '@vivli/features/data-requests/infrastructure/enum';
import { useDataRequestsApiContext, useModalApiContext } from '@vivli/core/infrastructure/context';
import { dataRequestGridCustomComponents, useDataRequestGridColumns } from './data-requests-grid-columns.hook';

// Rows fetched per request as the user scrolls. Each request costs a few seconds on the server
// (page query plus count), so a block covers several screens of 95px rows.
export const DATA_REQUESTS_BLOCK_SIZE = 50;

// Grid column id -> the server's whitelisted sort field. The title column sorts by DOI, as the
// client-side grid's idComparator did; Draft's hidden createdTime column sorts by creation.
const sortFieldByColId: Record<string, DataRequestSummarySortFieldEnum> = {
    customTitle: DataRequestSummarySortFieldEnum.DataRequestDoi,
    createdTime: DataRequestSummarySortFieldEnum.CreatedTime,
    updatedDate: DataRequestSummarySortFieldEnum.UpdatedDate
};

export const getDataRequestServerSort = (colId?: string, sort?: 'asc' | 'desc' | null) => ({
    sortField: sortFieldByColId[colId] ?? DataRequestSummarySortFieldEnum.DataRequestDoi,
    sortDescending: sort !== 'asc'
});

const loadingRowStyle: CSSProperties = {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '13px',
    color: '#6b6b6b'
};

const loadingIndicatorStyle: CSSProperties = { paddingTop: 0, height: '22px' };

// Shown in a row whose block is still being fetched while the user scrolls.
const LoadingRowPlaceholder = () => (
    <div style={loadingRowStyle} data-test-id="dataRequestRow_loading">
        <LoadIndicatorView size={LoadIndicatorSizeEnum.Small} style={loadingIndicatorStyle} />
        <span>Loading...</span>
    </div>
);

// The Infinite Row Model renders placeholder rows with no data while a block loads, and the shared
// renderers read data unconditionally. A placeholder row shows the loading indicator in its title
// column and nothing in its other columns.
const withLoadedRow = (Renderer: ComponentType<ICellRendererParams>, Placeholder?: ComponentType) => {
    const LoadedRowRenderer = (props: ICellRendererParams) => {
        if (props.data) {
            return <Renderer {...props} />;
        }

        return Placeholder ? <Placeholder /> : null;
    };
    return LoadedRowRenderer;
};

const pagedCustomComponents = dataRequestGridCustomComponents.map((c) => ({
    ...c,
    component: withLoadedRow(c.component, c.name === GridCellRendererEnum.DataRequestTitle ? LoadingRowPlaceholder : undefined)
}));

interface PagedDataRequestsGridComponentProps {
    assignedAppType: string;
    tab: DataRequestListTabEnum;
    search?: string;
    onRowClick?: (dataRequest: IDataRequestSummary) => void;
    onGridReady?: (gridApi: GridApi) => void;
    onTotalCountChange?: (count: number) => void;
    emptyMessage?: string;
    isPresumedResearcher?: boolean;
    initialSortField?: string;
    initialSortOrder?: 'asc' | 'desc';
}

// DEV-1003: the admin Data Requests grid, paged on the server. Same columns as
// DataRequestsGridComponent, but rows of one tab load in blocks as the user scrolls, through ag-grid's
// Infinite Row Model, and the tab's count comes from the server's total.
export const PagedDataRequestsGridComponent = ({
    assignedAppType,
    tab,
    search,
    onRowClick,
    onGridReady,
    onTotalCountChange,
    emptyMessage,
    isPresumedResearcher,
    initialSortField,
    initialSortOrder
}: PagedDataRequestsGridComponentProps) => {
    const dataRequestsApi = useDataRequestsApiContext();
    const modalService = useModalApiContext();
    const gridApiRef = useRef<GridApi>();
    const onTotalCountChangeRef = useRef(onTotalCountChange);
    const activeDatasourceRef = useRef<IDatasource>(null);

    useEffect(() => {
        onTotalCountChangeRef.current = onTotalCountChange;
    }, [onTotalCountChange]);

    const columns = useDataRequestGridColumns({ isPresumedResearcher, initialSortField, initialSortOrder });

    // A new datasource makes ag-grid drop its cache and start again from the first block,
    // so a tab or search change needs nothing more than this memo.
    const datasource = useMemo<IDatasource>(() => {
        const thisDatasource: IDatasource = {
            getRows: (params) => {
                const sort = params.sortModel?.[0];
                dataRequestsApi
                    .getDataRequestSummaryPage(assignedAppType, {
                        tab,
                        startRow: params.startRow,
                        endRow: params.endRow,
                        ...getDataRequestServerSort(sort?.colId, sort?.sort),
                        search: search?.trim() || undefined
                    })
                    .pipe(first())
                    .subscribe({
                        next: (page) => {
                            // ag-grid discards rows for a datasource it has replaced; do the same
                            // with the count and overlay, or a slow reply from an old search can
                            // overwrite the badge and cover rows that did load
                            params.successCallback(page.rows, page.totalCount);
                            if (activeDatasourceRef.current !== thisDatasource) {
                                return;
                            }

                            onTotalCountChangeRef.current?.(page.totalCount);
                            if (page.totalCount === 0) {
                                gridApiRef.current?.showNoRowsOverlay();
                            } else {
                                gridApiRef.current?.hideOverlay();
                            }
                        },
                        error: (error) => {
                            params.failCallback();
                            modalService.error(`An error happened getting Data Requests. Error received: ${error}`);
                        }
                    });
            }
        };
        return thisDatasource;
    }, [assignedAppType, tab, search]);

    useEffect(() => {
        activeDatasourceRef.current = datasource;
    }, [datasource]);

    const handleGridReady = (gridApi: GridApi) => {
        gridApiRef.current = gridApi;
        onGridReady && onGridReady(gridApi);
    };

    return (
        <GridView
            rowModelType="infinite"
            datasource={datasource}
            cacheBlockSize={DATA_REQUESTS_BLOCK_SIZE}
            getRowId={(params) => params.data.id}
            columns={columns}
            hideHeader={true}
            hideFloatingFilter={true}
            fullWidthColumns={true}
            customComponents={pagedCustomComponents}
            rowHeight={95}
            // a placeholder row that is still loading has no data to open
            onRowClick={(dataRequest: IDataRequestSummary) => dataRequest && onRowClick && onRowClick(dataRequest)}
            onGridReady={handleGridReady}
            emptyMessage={emptyMessage}
        />
    );
};
